import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BadgeIndianRupee,
  BookOpenText,
  ChevronDown,
  FileSearch,
  Flame,
  LineChart,
  Lock,
  Mail,
  MessageCircle,
  MoveRight,
  Quote,
  ShieldCheck,
  Sparkles,
  Sprout,
  UserCheck,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/*
 * The Money Gurukul — public landing page.
 * Route: `/` (public). Authenticated members are shown a "Go to dashboard" pill instead of the
 * generic Login CTA so a signed-in browser session isn't sent back through the login flow.
 * Design language: obsidian black background, ambient gold radial glows, serif-display headings
 * paired with a sans body, and a subtle grain overlay to keep gradients from looking flat.
 */

const TAGLINE_WORDS = ["Learn.", "Earn.", "Grow.", "Multiply."];

const PILLARS = [
  {
    icon: Flame,
    title: "FIRE calculators",
    body: "Compute your Lean, Fat and Coast FIRE numbers with inflation-adjusted projections and see the year your assets outrun your lifestyle.",
  },
  {
    icon: FileSearch,
    title: "Policy analysis",
    body: "Feed us your LIC, Kotak or ULIP statement — password-protected PDFs welcome — and get a clean breakdown of returns, charges and better alternatives.",
  },
  {
    icon: BookOpenText,
    title: "Document AI extraction",
    body: "Upload account statements, invoices or contracts. Our Gemini-powered engine surfaces the numbers, dates and clauses that actually matter.",
  },
  {
    icon: Sparkles,
    title: "Money-beliefs quiz",
    body: "A 6-question archetype quiz surfaces the money story you inherited — so you can rewrite the parts that keep you stuck.",
  },
];

const STEPS = [
  {
    n: "01",
    icon: UserCheck,
    title: "Request access",
    body: "Send us your details from the form below. We personally review every applicant to keep the Gurukul focused.",
  },
  {
    n: "02",
    icon: Lock,
    title: "Login by OTP",
    body: "Once enrolled, log in with a one-time code sent to your email or WhatsApp. No passwords to forget.",
  },
  {
    n: "03",
    icon: LineChart,
    title: "Learn, Earn, Grow, Multiply",
    body: "Use the calculators, upload your policies, take the quiz — build a wealth plan that survives real Indian markets.",
  },
];

const TESTIMONIALS = [
  {
    name: "Ritika S.",
    role: "IT Manager, Pune",
    body: "The FIRE projection made me realise I was 4 years closer than I thought — but my LIC endowment was quietly costing me a year of freedom. Cancelled it the same week.",
  },
  {
    name: "Aditya P.",
    role: "Founder, D2C brand",
    body: "The document AI pulled 3 exit charges out of a ULIP that my own agent never explained. That single screen paid for a decade of Gurukul access.",
  },
  {
    name: "Meera K.",
    role: "Doctor, Ahmedabad",
    body: "The money-beliefs quiz felt eerily accurate. I was the classic 'Vigilant' — over-saving, under-investing. The nudges after are what moved the needle.",
  },
];

const FAQS = [
  {
    q: "Who can join The Money Gurukul?",
    a: "Enrolment is currently invite-only. Fill the Request Access form below with a short note about your goals and our founder will personally get back to you within 48 hours.",
  },
  {
    q: "Is this SEBI-registered investment advice?",
    a: "No. Everything here is educational — calculators, extractors, quizzes. We help you understand your own numbers so you can have sharper conversations with your CA or SEBI-RIA. We do not sell products or take commissions.",
  },
  {
    q: "How is my data kept safe?",
    a: "Login is OTP-only (no passwords stored). Uploaded PDFs are decrypted in memory and only the extracted summary is retained — the raw file is dropped. All third-party API keys are encrypted at rest with Fernet.",
  },
  {
    q: "Do you support password-protected policy PDFs?",
    a: "Yes. Enter the PDF password inside the upload dialog; we open it in a sandboxed process and never persist the password itself.",
  },
  {
    q: "What does membership cost?",
    a: "Pricing is discussed one-on-one during onboarding — we tailor access to whether you're an early-career saver, a mid-career optimiser, or a business owner planning succession.",
  },
];

/* --------------------------------- Page --------------------------------- */

export function LandingPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const primaryCta = isAuthenticated ? "/app" : "/login";

  // Force dark theme while on the landing page — the marketing aesthetic is dark-only, and
  // regardless of the member's saved preference we want the hero + glows to render on obsidian.
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.classList.contains("light") ? "light" : "dark";
    html.classList.remove("light");
    html.classList.add("dark");
    return () => {
      html.classList.remove("dark", "light");
      html.classList.add(prev);
    };
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0B0B0C] text-[#F4F1E8]">
      <AmbientGlows />
      <GrainOverlay />
      <TopNav primaryCta={primaryCta} isAuthenticated={isAuthenticated} />

      <main className="relative">
        <Hero primaryCta={primaryCta} isAuthenticated={isAuthenticated} />
        <PillarsSection />
        <HowItWorksSection />
        <AboutSection />
        <TestimonialsSection />
        <FaqSection />
        <RequestAccessSection onLogin={() => navigate(primaryCta)} />
        <Footer />
      </main>
    </div>
  );
}

/* -------------------------------- Nav --------------------------------- */

function TopNav({ primaryCta, isAuthenticated }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="landing-nav"
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "border-b border-[#D4AF37]/15 bg-[#0B0B0C]/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
        <a href="#top" className="group flex items-center gap-3" data-testid="landing-brand">
          <img
            src="/logo-mark.png"
            alt="The Money Gurukul mark"
            className="h-9 w-9 object-contain transition-transform duration-300 group-hover:rotate-6"
          />
          <span className="hidden font-serif text-lg tracking-wide text-[#F4F1E8] sm:inline">
            The <span className="text-[#D4AF37]">Money</span> Gurukul
          </span>
        </a>

        <nav className="hidden items-center gap-8 md:flex">
          <NavAnchor href="#pillars">What you get</NavAnchor>
          <NavAnchor href="#how">How it works</NavAnchor>
          <Link
            to="/page/about"
            data-testid="landing-nav-about"
            className="relative text-sm text-[#9A968C] transition-colors hover:text-[#F4F1E8]"
          >
            About Us
          </Link>
          <Link
            to="/page/contact"
            data-testid="landing-nav-contact"
            className="relative text-sm text-[#9A968C] transition-colors hover:text-[#F4F1E8]"
          >
            Contact Us
          </Link>
          <NavAnchor href="#faq">FAQ</NavAnchor>
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="#request"
            className="hidden rounded-full border border-[#D4AF37]/40 px-4 py-2 text-xs font-medium uppercase tracking-widest text-[#D4AF37] transition-all hover:bg-[#D4AF37]/10 sm:inline-block"
            data-testid="landing-request-access-nav"
          >
            Request access
          </a>
          <Link
            to={primaryCta}
            data-testid="landing-login-nav"
            className="group relative inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#E8C766] px-4 py-2 text-xs font-semibold uppercase tracking-widest text-[#1A1400] shadow-[0_0_30px_-8px_rgba(212,175,55,0.7)] transition-all hover:shadow-[0_0_40px_-6px_rgba(212,175,55,0.9)]"
          >
            {isAuthenticated ? "Dashboard" : "Login"}
            <MoveRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </header>
  );
}

function NavAnchor({ href, children }) {
  return (
    <a
      href={href}
      className="relative text-sm text-[#9A968C] transition-colors hover:text-[#F4F1E8]"
    >
      {children}
    </a>
  );
}

/* -------------------------------- Hero -------------------------------- */

function Hero({ primaryCta, isAuthenticated }) {
  return (
    <section id="top" className="relative px-5 pt-32 pb-28 sm:px-8 sm:pt-36 md:pt-40">
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-16 md:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
        {/* Copy */}
        <div className="relative">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">
            <Sparkles className="h-3 w-3" />
            Invite-only wealth atelier
          </div>

          <h1
            className="font-serif text-4xl leading-[1.05] text-[#F4F1E8] sm:text-5xl lg:text-6xl"
            data-testid="landing-hero-headline"
          >
            An ancient <span className="italic text-[#D4AF37]">gurukul</span>
            <br />
            for modern money.
          </h1>

          <p className="mt-6 max-w-lg text-base leading-relaxed text-[#9A968C] sm:text-lg">
            The Money Gurukul is a private space where Indian professionals decode their policies,
            compute their FIRE numbers, and rewrite the money beliefs holding them back — with
            calculators built for real markets, not motivational quotes.
          </p>

          {/* Tagline — each word reveals in sequence */}
          <div className="mt-8 flex flex-wrap items-baseline gap-x-3 gap-y-1 font-serif text-2xl text-[#F4F1E8] sm:text-3xl">
            {TAGLINE_WORDS.map((w, i) => (
              <span
                key={w}
                className="animate-fade-up bg-gradient-to-b from-[#E8C766] to-[#B8912E] bg-clip-text text-transparent"
                style={{ animationDelay: `${300 + i * 140}ms` }}
              >
                {w}
              </span>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link to={primaryCta} data-testid="landing-hero-cta-primary">
              <Button
                size="lg"
                className="rounded-full bg-gradient-to-r from-[#D4AF37] to-[#E8C766] px-7 py-6 text-sm font-semibold uppercase tracking-widest text-[#1A1400] shadow-[0_0_40px_-8px_rgba(212,175,55,0.8)] transition-all hover:translate-y-[-1px] hover:shadow-[0_0_60px_-4px_rgba(232,199,102,0.9)]"
              >
                {isAuthenticated ? "Go to dashboard" : "Enter the gurukul"}
                <MoveRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a
              href="#request"
              data-testid="landing-hero-cta-secondary"
              className="rounded-full border border-[#D4AF37]/30 px-7 py-3.5 text-sm font-medium uppercase tracking-widest text-[#D4AF37] transition-all hover:border-[#D4AF37] hover:bg-[#D4AF37]/5"
            >
              Request access
            </a>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-[#9A968C]">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-[#D4AF37]" />
              OTP login, no passwords
            </span>
            <span className="inline-flex items-center gap-1.5">
              <BadgeIndianRupee className="h-3.5 w-3.5 text-[#D4AF37]" />
              Built for ₹ Indian markets
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Sprout className="h-3.5 w-3.5 text-[#D4AF37]" />
              Educational · not commissioned
            </span>
          </div>
        </div>

        {/* Visual */}
        <HeroVisual />
      </div>
    </section>
  );
}

function HeroVisual() {
  return (
    <div className="relative mx-auto aspect-square w-full max-w-md">
      {/* Concentric gold rings */}
      <div className="absolute inset-0 animate-spin-slow rounded-full border border-[#D4AF37]/20" />
      <div
        className="absolute inset-6 rounded-full border border-[#D4AF37]/15"
        style={{ animation: "spin-slow 40s linear infinite reverse" }}
      />
      <div className="absolute inset-14 rounded-full border border-dashed border-[#D4AF37]/10" />

      {/* Gold coin glow behind logo */}
      <div className="absolute inset-16 rounded-full bg-gradient-to-br from-[#D4AF37]/25 via-[#B8912E]/10 to-transparent blur-2xl" />

      {/* Central logo card */}
      <div className="absolute inset-16 flex items-center justify-center rounded-full border border-[#D4AF37]/30 bg-[#17171A]/60 shadow-[inset_0_0_60px_-10px_rgba(212,175,55,0.15)] backdrop-blur-xl">
        <img
          src="/logo-mark.png"
          alt="The Money Gurukul emblem"
          className="h-3/5 w-3/5 object-contain drop-shadow-[0_0_30px_rgba(212,175,55,0.5)]"
        />
      </div>

      {/* Floating metric chips */}
      <FloatingChip
        className="absolute -left-4 top-16"
        icon={Flame}
        label="Lean FIRE"
        value="₹2.4 Cr"
      />
      <FloatingChip
        className="absolute -right-2 top-32"
        icon={LineChart}
        label="XIRR"
        value="14.8%"
      />
      <FloatingChip
        className="absolute -bottom-2 left-1/2 -translate-x-1/2"
        icon={BadgeIndianRupee}
        label="Corpus @ 55"
        value="₹6.9 Cr"
      />
    </div>
  );
}

function FloatingChip({ className = "", icon: Icon, label, value }) {
  return (
    <div
      className={`animate-float rounded-2xl border border-[#D4AF37]/25 bg-[#17171A]/85 px-3 py-2 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)] backdrop-blur-md ${className}`}
    >
      <div className="flex items-center gap-2">
        <div className="rounded-lg bg-[#D4AF37]/15 p-1.5">
          <Icon className="h-3.5 w-3.5 text-[#E8C766]" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#9A968C]">{label}</div>
          <div className="font-serif text-sm text-[#F4F1E8]">{value}</div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ Pillars ------------------------------ */

function PillarsSection() {
  return (
    <section id="pillars" className="relative px-5 py-24 sm:px-8" data-testid="landing-pillars">
      <div className="mx-auto max-w-6xl">
        <SectionKicker label="What you get" />
        <SectionHeadline>
          Four disciplines. <span className="italic text-[#D4AF37]">One</span> financial life.
        </SectionHeadline>
        <p className="mt-4 max-w-2xl text-[#9A968C]">
          Everything inside the gurukul is built to answer the questions your bank RM won&apos;t:
          <em> &ldquo;Am I actually on track?&rdquo;</em> and{" "}
          <em> &ldquo;Is this policy still worth keeping?&rdquo;</em>
        </p>

        <div className="mt-14 grid gap-4 sm:grid-cols-2">
          {PILLARS.map((p) => (
            <PillarCard key={p.title} {...p} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PillarCard({ icon: Icon, title, body }) {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-[#2A2A2E] bg-gradient-to-br from-[#17171A] via-[#141416] to-[#0F0F11] p-7 transition-all duration-500 hover:border-[#D4AF37]/40 hover:shadow-[0_20px_60px_-30px_rgba(212,175,55,0.6)]">
      <div
        className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-[#D4AF37]/10 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100"
        aria-hidden
      />
      <div className="relative">
        <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#E8C766]">
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="font-serif text-xl text-[#F4F1E8]">{title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-[#9A968C]">{body}</p>
      </div>
    </div>
  );
}

/* ---------------------------- How it works ---------------------------- */

function HowItWorksSection() {
  return (
    <section id="how" className="relative px-5 py-24 sm:px-8">
      <div className="mx-auto max-w-6xl">
        <SectionKicker label="How it works" />
        <SectionHeadline>
          Three <span className="italic text-[#D4AF37]">simple</span> gates to enter.
        </SectionHeadline>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <div key={s.n} className="relative">
              {i < STEPS.length - 1 && (
                <div
                  aria-hidden
                  className="absolute left-full top-8 hidden h-px w-full -translate-x-6 bg-gradient-to-r from-[#D4AF37]/40 to-transparent md:block"
                />
              )}
              <div className="rounded-2xl border border-[#2A2A2E] bg-[#17171A]/60 p-7 backdrop-blur-sm transition-colors hover:border-[#D4AF37]/30">
                <div className="flex items-center justify-between">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#E8C766]">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <span className="font-serif text-4xl text-[#D4AF37]/40">{s.n}</span>
                </div>
                <h3 className="mt-6 font-serif text-xl text-[#F4F1E8]">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#9A968C]">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- About ------------------------------- */

function AboutSection() {
  return (
    <section id="about" className="relative px-5 py-24 sm:px-8">
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-14 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <div className="relative">
          <div className="relative aspect-square w-full max-w-sm overflow-hidden rounded-3xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#1F1F24] via-[#141416] to-[#0B0B0C]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(212,175,55,0.18),transparent_60%)]" />
            <img
              src="/logo-with-text.png"
              alt="The Money Gurukul"
              className="absolute inset-6 h-[calc(100%-3rem)] w-[calc(100%-3rem)] object-contain drop-shadow-[0_0_40px_rgba(212,175,55,0.35)]"
            />
          </div>
        </div>
        <div>
          <SectionKicker label="The founder's note" />
          <SectionHeadline>
            Built by <span className="italic text-[#D4AF37]">someone</span> who paid the tuition.
          </SectionHeadline>
          <div className="mt-6 space-y-4 text-[#9A968C]">
            <p>
              I&apos;ve held nearly every mis-sold insurance policy an Indian household can hold. LIC
              endowments in college. Unit-linked plans in my first job. A ULIP disguised as a
              &ldquo;child plan&rdquo; the moment my daughter was born. Together they quietly cost me a decade
              of compounding.
            </p>
            <p>
              The Money Gurukul is the toolkit I wish I had — calculators grounded in Indian tax
              and inflation reality, extractors that read the fine print your agent glossed over,
              and a curriculum that respects that money is 20% arithmetic and 80% behaviour.
            </p>
            <p className="text-[#F4F1E8]">
              — Parth, founder ·{" "}
              <span className="font-serif italic text-[#D4AF37]">The Money Gurukul</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- Testimonials ---------------------------- */

function TestimonialsSection() {
  return (
    <section className="relative px-5 py-24 sm:px-8">
      <div className="mx-auto max-w-6xl">
        <SectionKicker label="From the shishyas" />
        <SectionHeadline>
          Small nudges. <span className="italic text-[#D4AF37]">Life-sized</span> outcomes.
        </SectionHeadline>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {TESTIMONIALS.map((t) => (
            <figure
              key={t.name}
              className="relative flex h-full flex-col rounded-2xl border border-[#2A2A2E] bg-gradient-to-br from-[#17171A] to-[#0F0F11] p-7"
            >
              <Quote className="h-6 w-6 text-[#D4AF37]/50" />
              <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-[#F4F1E8]/90">
                {t.body}
              </blockquote>
              <figcaption className="mt-6 border-t border-[#2A2A2E] pt-4">
                <div className="font-serif text-[#D4AF37]">{t.name}</div>
                <div className="text-xs text-[#9A968C]">{t.role}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------- FAQ -------------------------------- */

function FaqSection() {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section id="faq" className="relative px-5 py-24 sm:px-8">
      <div className="mx-auto max-w-3xl">
        <SectionKicker label="Frequently asked" />
        <SectionHeadline center>
          Everything <span className="italic text-[#D4AF37]">before</span> you knock.
        </SectionHeadline>
        <div className="mt-12 divide-y divide-[#2A2A2E] rounded-2xl border border-[#2A2A2E] bg-[#0F0F11]/60">
          {FAQS.map((f, i) => {
            const open = i === openIdx;
            return (
              <button
                key={f.q}
                type="button"
                onClick={() => setOpenIdx(open ? -1 : i)}
                className="block w-full px-6 py-5 text-left transition-colors hover:bg-[#D4AF37]/[0.03]"
                data-testid={`landing-faq-item-${i}`}
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="font-serif text-lg text-[#F4F1E8]">{f.q}</span>
                  <ChevronDown
                    className={`h-4 w-4 flex-shrink-0 text-[#D4AF37] transition-transform duration-300 ${
                      open ? "rotate-180" : ""
                    }`}
                  />
                </div>
                <div
                  className={`grid transition-[grid-template-rows,opacity] duration-300 ${
                    open ? "mt-3 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <p className="overflow-hidden text-sm leading-relaxed text-[#9A968C]">{f.a}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* --------------------------- Request access --------------------------- */

function RequestAccessSection() {
  const [form, setForm] = useState({ name: "", email: "", mobile: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  function set(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/contact/request-access", {
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim() || null,
        message: form.message.trim() || null,
      });
      setDone(true);
      toast.success("Request received. We'll be in touch within 48 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="request" className="relative px-5 py-24 sm:px-8">
      <div className="mx-auto max-w-4xl">
        <div className="relative overflow-hidden rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#17171A] via-[#141416] to-[#0B0B0C] p-8 sm:p-12">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#B8912E]/10 blur-3xl"
          />

          <div className="relative grid gap-10 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
            <div>
              <SectionKicker label="Request access" />
              <h2 className="mt-3 font-serif text-3xl leading-tight text-[#F4F1E8] sm:text-4xl">
                Knock on the <span className="italic text-[#D4AF37]">gurukul&apos;s</span> door.
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-[#9A968C]">
                Enrolment is invite-only and reviewed by the founder personally. Tell us where
                you are in your money journey and what you&apos;d like to fix — we usually respond
                within 48 hours.
              </p>

              <div className="mt-8 space-y-3 text-sm text-[#9A968C]">
                <ContactLine icon={Mail} label="hello@themoneygurukul.com" />
                <ContactLine icon={MessageCircle} label="WhatsApp on request" />
              </div>
            </div>

            {done ? (
              <div
                data-testid="landing-request-success"
                className="flex flex-col items-center justify-center rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.04] p-8 text-center"
              >
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#D4AF37]/15">
                  <Sparkles className="h-5 w-5 text-[#E8C766]" />
                </div>
                <h3 className="font-serif text-xl text-[#F4F1E8]">Request received</h3>
                <p className="mt-2 max-w-sm text-sm text-[#9A968C]">
                  We&apos;ve noted your details and will personally reply from the founder&apos;s inbox
                  within 48 hours.
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="flex flex-col gap-4"
                data-testid="landing-request-form"
              >
                <div>
                  <Label htmlFor="ra-name" className="text-xs uppercase tracking-widest text-[#9A968C]">
                    Full name
                  </Label>
                  <Input
                    id="ra-name"
                    data-testid="landing-request-name"
                    value={form.name}
                    onChange={set("name")}
                    required
                    className="mt-1.5 border-[#2A2A2E] bg-[#0B0B0C]/60 text-[#F4F1E8] focus-visible:ring-[#D4AF37]"
                    placeholder="Meera Kapoor"
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="ra-email" className="text-xs uppercase tracking-widest text-[#9A968C]">
                      Email
                    </Label>
                    <Input
                      id="ra-email"
                      data-testid="landing-request-email"
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      required
                      className="mt-1.5 border-[#2A2A2E] bg-[#0B0B0C]/60 text-[#F4F1E8] focus-visible:ring-[#D4AF37]"
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ra-mobile" className="text-xs uppercase tracking-widest text-[#9A968C]">
                      WhatsApp <span className="normal-case text-[#6E685C]">(optional)</span>
                    </Label>
                    <Input
                      id="ra-mobile"
                      data-testid="landing-request-mobile"
                      value={form.mobile}
                      onChange={set("mobile")}
                      placeholder="+91 98••• •••••"
                      className="mt-1.5 border-[#2A2A2E] bg-[#0B0B0C]/60 text-[#F4F1E8] focus-visible:ring-[#D4AF37]"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="ra-msg" className="text-xs uppercase tracking-widest text-[#9A968C]">
                    What are you looking to solve? <span className="normal-case text-[#6E685C]">(optional)</span>
                  </Label>
                  <textarea
                    id="ra-msg"
                    data-testid="landing-request-message"
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="e.g. Reviewing a 15-year LIC policy, need FIRE plan, etc."
                    className="mt-1.5 w-full rounded-md border border-[#2A2A2E] bg-[#0B0B0C]/60 px-3 py-2 text-sm text-[#F4F1E8] placeholder:text-[#6E685C] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="landing-request-submit"
                  className="mt-2 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#E8C766] py-6 text-sm font-semibold uppercase tracking-widest text-[#1A1400] shadow-[0_0_40px_-8px_rgba(212,175,55,0.8)] transition-all hover:translate-y-[-1px] hover:shadow-[0_0_60px_-4px_rgba(232,199,102,0.9)]"
                >
                  {submitting ? "Sending…" : "Request access"}
                  <MoveRight className="ml-2 h-4 w-4" />
                </Button>
                <p className="text-center text-xs text-[#6E685C]">
                  We never share your details. Reviewed by the founder personally.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactLine({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2">
      <div className="rounded-md border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-1.5">
        <Icon className="h-3.5 w-3.5 text-[#D4AF37]" />
      </div>
      <span>{label}</span>
    </div>
  );
}

/* -------------------------------- Footer ------------------------------- */

const LEGAL_LINKS = [
  { slug: "about", label: "About Us" },
  { slug: "contact", label: "Contact Us" },
  { slug: "privacy", label: "Privacy Policy" },
  { slug: "refund", label: "Refund Policy" },
  { slug: "terms", label: "Terms & Conditions" },
];

function Footer() {
  const year = useMemo(() => new Date().getFullYear(), []);
  return (
    <footer
      className="relative border-t border-[#2A2A2E] px-5 py-12 sm:px-8"
      data-testid="landing-footer"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-8">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <div className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="" className="h-8 w-8 object-contain" />
            <div>
              <div className="font-serif text-lg text-[#F4F1E8]">
                The <span className="text-[#D4AF37]">Money</span> Gurukul
              </div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#9A968C]">
                Learn · Earn · Grow · Multiply
              </div>
            </div>
          </div>
          <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-[#9A968C]">
            <a href="#pillars" className="hover:text-[#F4F1E8]">
              What you get
            </a>
            <a href="#how" className="hover:text-[#F4F1E8]">
              How it works
            </a>
            <a href="#faq" className="hover:text-[#F4F1E8]">
              FAQ
            </a>
            <a href="#request" className="hover:text-[#F4F1E8]">
              Request access
            </a>
          </nav>
        </div>

        <div className="border-t border-[#2A2A2E] pt-6">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
            <nav
              className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs"
              aria-label="Legal and information"
            >
              {LEGAL_LINKS.map((l) => (
                <Link
                  key={l.slug}
                  to={`/page/${l.slug}`}
                  data-testid={`landing-footer-link-${l.slug}`}
                  className="text-[#9A968C] transition-colors hover:text-[#D4AF37]"
                >
                  {l.label}
                </Link>
              ))}
            </nav>
            <div className="text-xs text-[#6E685C]">
              © {year} The Money Gurukul · Educational, not investment advice.
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ------------------------- Shared micro elements ----------------------- */

function SectionKicker({ label }) {
  return (
    <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#D4AF37]">
      <span className="h-px w-6 bg-[#D4AF37]/60" />
      {label}
    </div>
  );
}

function SectionHeadline({ children, center }) {
  return (
    <h2
      className={`mt-3 font-serif text-3xl leading-tight text-[#F4F1E8] sm:text-4xl lg:text-5xl ${
        center ? "text-center" : ""
      }`}
    >
      {children}
    </h2>
  );
}

function AmbientGlows() {
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none fixed left-[-10%] top-[-10%] h-[520px] w-[520px] rounded-full bg-[#D4AF37]/15 blur-[140px]"
      />
      <div
        aria-hidden
        className="pointer-events-none fixed right-[-15%] top-[40%] h-[600px] w-[600px] rounded-full bg-[#B8912E]/10 blur-[160px]"
      />
    </>
  );
}

function GrainOverlay() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[1] opacity-[0.035] mix-blend-overlay"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
      }}
    />
  );
}

export default LandingPage;
